import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import { TranslocoModule } from '@jsverse/transloco';
import { Observable, Subject, debounceTime, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs';

import { Option } from '@hmax-order/shared/types';
import { CapitalizePipe } from '@hmax-order/frontend/utils';
import { ValidateFieldsService } from '../validate-fields.service';

@Component({
  selector: 'hmax-input-text-autocomplete',
  standalone: true,
  templateUrl: './input-text-autocomplete.component.html',
  styleUrls: ['./input-text-autocomplete.component.scss'],
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CapitalizePipe,
    CommonModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    MatChipsModule,
    TranslocoModule,
  ],
})
export class InputTextAutocompleteComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private readonly onDestroy: Subject<void> = new Subject<void>();

  @ViewChild(MatAutocompleteTrigger) trigger!: MatAutocompleteTrigger;

  @Input() label!: string;
  @Input() translateLabel = true;
  @Input() placeholder!: string;
  @Input() icon!: string;
  @Input() options!: Option[];
  @Input() formGroup!: FormGroup;
  @Input() controlName!: string;
  @Input() autocomplete = '';
  @Input() selectedOption = true; // Definir que o usuário deve escolher uma opção da lista
  @Input() appearance: 'outline' | 'fill' = 'outline';
  @Input() maxLength!: number;
  /** Iniciar com o popup se auto seleção aberto */
  @Input() startOpened!: boolean;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options$?: Observable<any[]>;
  disableAutocomplete = !this.startOpened;

  get formControl(): AbstractControl {
    return this.formGroup?.controls[this.controlName];
  }

  public validateField = inject(ValidateFieldsService);

  ngOnInit(): void {
    this.loadOptions();
    this.subscribeField();
    // Precisa ser esta comparação para continuar abrindo popup quando a tag startOpened não for informada
    // mas não abrir quando for informada com false
    this.disableAutocomplete = this.startOpened === false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.loadOptions();
    }
  }

  ngAfterViewInit(): void {
    // Limpar a opção informada se não existir na lista de opções para ser selecionado
    if (this.selectedOption) {
      this.trigger?.panelClosingActions.subscribe((e) => {
        if (!(e && e?.source) && !this.existValueInOptions()) {
          this.formControl.setValue('');
          this.trigger.closePanel();
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  /**
   * Retornar a formação de como deve ser exibido no
   * campo input para o usuário.
   * @param Options Opções
   * @returns Como deve aparecer no campo, quando um item form selecionado
   */
  displaySelect(option: Option): string {
    return option?.name ? option?.name : '';
  }

  /** Evento acionado quando clicamos no input */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onClick(event: Event) {
    // Reativar autocomplete
    this.disableAutocomplete = false;
    // Força abertura do menu
    this.trigger.openPanel();
  }

  /** Evento acionado quando algo é alterado no input */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange(event: Event) {
    // Reativar autocomplete
    this.disableAutocomplete = false;
    this.trigger.openPanel();
  }

  /**
   * Inscrever para observar alteração no campo do formulário
   */
  private subscribeField(): void {
    this.formControl.valueChanges.pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.onDestroy)).subscribe((option: Option) => {
      this.isString(option);
    });
  }

  /**
   * Verificar se o valor é uma string
   * @param option Valor do campo
   */
  private isString(option: Option): void {
    if (typeof option === 'string') {
      this.hasOptionInList(option);
    }
  }

  /**
   * Verificar se existe uma única opção com o texto exato informado pelo usuário,
   * Se existir preenche o valor do formulário com a opção
   * @param value Valor do campo
   */
  private hasOptionInList(value: string): void {
    const matchingOptions: Option[] = this.options.filter(
      (option) =>
        option?.name?.toLowerCase() === value.toLowerCase() ||
        option?.value?.name?.toLowerCase() === value.toLowerCase() ||
        option?.value?.stateCode?.toLowerCase() === value.toLowerCase(),
    );

    if (matchingOptions?.length === 1) this.formControl.setValue(matchingOptions[0]);
  }

  /**
   * Carregar opções do select com as opções informadas
   */
  private loadOptions(): void {
    // Retorna um observable com base nos valores informados no input
    this.options$ = this.formControl?.valueChanges.pipe(
      startWith(''),
      debounceTime(400),
      distinctUntilChanged(),
      map((value) => {
        const name = typeof value === 'string' ? value : value?.name;

        return name ? this.filter(name as string) : this.options;
      }),
    );
  }

  /**
   * Filtrar opções a medida que digitamos no select
   * @param value Valor do input para ser filtrado
   */
  private filter(value: string): Option[] {
    const valueLower = value.toLowerCase();

    return this.options?.filter((option: Option) => {
      return option?.name?.toLowerCase().includes(valueLower) || option?.value === value;
    });
  }

  /**
   * Verificar se a opção já seleciona existe na lista de opções
   */
  private existValueInOptions(): boolean {
    const valueInInput = this.formControl.value?.value || this.formControl.value;

    const exist = this.options?.findIndex((option: Option) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const valueOption = option?.value as any;

      return option?.value === valueInInput || (valueOption?.id === valueInInput?.id && valueOption?.id != null);
    });

    return exist !== -1;
  }
}
